import React, { useState, useEffect } from "react";
import axios from "axios";
import { BarChart3 } from "lucide-react";
import { Tile, Card, HBar, EmptyState, Loading, fmtN } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Analytics({ headers, range, refreshKey, onGoConnections }) {
  const [ga, setGa] = useState(null);

  useEffect(() => {
    setGa(null);
    axios
      .get(`${API}/admin/dashboard/ga4?range=${range}`, headers())
      .then((r) => setGa(r.data))
      .catch(() => setGa({ connected: false, measurement_id: "G-5Q3L96NZ5P" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, refreshKey]);

  if (!ga) return <Loading />;

  if (!ga.connected) {
    return (
      <div className="mt-6" data-testid="analytics-tab">
        <EmptyState icon={BarChart3} title="Connect Google Analytics 4" testid="ga4-empty-state">
          {ga.error || "GA4 isn't connected yet, so sessions, users and conversion data can't load."} The site is already tagged with measurement ID{" "}
          <span className="font-mono text-white" data-testid="ga4-measurement-id">{ga.measurement_id}</span> — data is collecting in Google Analytics; this dashboard just needs API access.{" "}
          <button onClick={onGoConnections} data-testid="ga4-go-connections" className="text-[#E56A79] hover:text-white underline underline-offset-2 transition-colors">
            Open Connections for setup steps →
          </button>
        </EmptyState>
      </div>
    );
  }

  const maxCh = Math.max(1, ...ga.channels.map((c) => c.sessions));
  const maxLp = Math.max(1, ...ga.landing_pages.map((c) => c.sessions));

  return (
    <div className="mt-6 space-y-5" data-testid="analytics-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
        <Tile testid="ga4-sessions" label="Sessions" value={fmtN(ga.summary.sessions)} sub={`${ga.start} → ${ga.end}`} />
        <Tile testid="ga4-users" label="Users" value={fmtN(ga.summary.users)} />
        <Tile testid="ga4-engaged" label="Engaged Sessions" value={fmtN(ga.summary.engaged_sessions)} />
        <Tile testid="ga4-leads" label="generate_lead Events" value={fmtN(ga.generate_lead)} sub={`${ga.conversion_rate}% of sessions`} />
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <Card title="Exit Popup Funnel" testid="ga4-popup-card">
          <div className="space-y-4">
            {[
              ["Popup views (exit_popup_view)", ga.popup.views],
              ["Popup leads (form saved)", ga.popup.leads],
            ].map(([label, v]) => (
              <div key={label}>
                <div className="flex items-baseline justify-between mb-1.5">
                  <span className="text-[12px] text-neutral-300">{label}</span>
                  <span className="font-heading font-extrabold text-lg text-white">{fmtN(v)}</span>
                </div>
                <HBar pct={ga.popup.views ? (v / Math.max(1, ga.popup.views)) * 100 : 0} />
              </div>
            ))}
            <div className="pt-2 border-t border-white/5">
              <span className="font-heading font-extrabold text-2xl text-[#E56A79]" data-testid="ga4-popup-cvr">{ga.popup.cvr}%</span>
              <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.14em] text-neutral-500">popup conversion rate</span>
            </div>
          </div>
        </Card>

        <Card title="Channels" testid="ga4-channels">
          {ga.channels.length === 0 && <p className="text-[13px] text-neutral-500">No sessions in range.</p>}
          <div className="space-y-3">
            {ga.channels.map((c) => (
              <div key={c.channel}>
                <div className="flex items-baseline justify-between mb-1">
                  <span className="text-[12px] text-neutral-200">{c.channel}</span>
                  <span className="font-mono text-[10px] text-neutral-500">{fmtN(c.sessions)} sessions · {fmtN(c.users)} users</span>
                </div>
                <HBar pct={(c.sessions / maxCh) * 100} />
              </div>
            ))}
          </div>
        </Card>

        <Card title="Top Landing Pages" testid="ga4-landing">
          {ga.landing_pages.length === 0 && <p className="text-[13px] text-neutral-500">No sessions in range.</p>}
          <div className="space-y-3">
            {ga.landing_pages.map((p) => (
              <div key={p.page}>
                <div className="flex items-baseline justify-between gap-3 mb-1">
                  <span className="text-[12px] text-neutral-200 truncate" title={p.page}>{p.page}</span>
                  <span className="font-mono text-[10px] text-neutral-500 whitespace-nowrap">{fmtN(p.sessions)}</span>
                </div>
                <HBar pct={(p.sessions / maxLp) * 100} />
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
